import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, getAuthHeaders } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      setCart({ items: [], total: 0 });
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      setCart({ items: [], total: 0 });
    }
  }, [isAuthenticated, fetchCart]);

  const addToCart = async (productId, productType) => {
    if (!isAuthenticated) {
      return { success: false, error: 'Please login first' };
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API}/cart/add`,
        { product_id: productId, product_type: productType, quantity: 1 },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setCart(response.data.cart);
      await fetchCart();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to add to cart' };
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    try {
      setLoading(true);
      const response = await axios.delete(`${API}/cart/remove/${productId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setCart(response.data.cart);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to remove from cart' };
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      await axios.delete(`${API}/cart/clear`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setCart({ items: [], total: 0 });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to clear cart' };
    } finally {
      setLoading(false);
    }
  };

  const cartItemCount = cart.items?.length || 0;

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        fetchCart,
        addToCart,
        removeFromCart,
        clearCart,
        cartItemCount
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
